import { ChevronRight } from "lucide-react";
import { cloneElement, type ReactElement } from "react";
import { NavLink } from "react-router";

export interface ListNavItem {
  to: string;
  label: string;
  icon?: ReactElement<{ size?: number; className?: string }>;
  /** Hide the item (capability or role gate) without callers filtering arrays. */
  hidden?: boolean;
  /** Active only on exactly `to` (for an item whose path prefixes its siblings', e.g. /admin/usage). */
  end?: boolean;
}

export interface ListNavGroup {
  id: string;
  label: string;
  items: ListNavItem[];
}

export interface ListNavProps {
  groups: ListNavGroup[];
  "aria-label": string;
  /** `column`: compact desktop nav column. `list`: the mobile drill-in home (bigger rows, chevrons). */
  variant?: "column" | "list";
}

/** Visible groups with their visible items; groups left empty are dropped. */
export function visibleGroups(groups: ListNavGroup[]): ListNavGroup[] {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => !item.hidden) }))
    .filter((group) => group.items.length > 0);
}

// Same active-row idiom as the design system's SidebarNav: filled bg + 2px accent bar.
const ACTIVE_ROW =
  "relative bg-surface-elevated shadow-sm text-content-primary [font-weight:var(--weight-medium)] before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-content-primary";

const COLUMN_ROW =
  "flex w-full items-center gap-3 rounded-lg px-3 py-2 [font:var(--type-body-xs)] transition-colors btn-focus-ring";
const LIST_ROW =
  "flex w-full items-center gap-3 px-4 py-3.5 [font:var(--type-body)] text-content-primary transition-colors btn-focus-ring hover:bg-surface-secondary";

function columnRowClass({ isActive }: { isActive: boolean }) {
  return `${COLUMN_ROW} ${isActive ? ACTIVE_ROW : "text-content-secondary hover:bg-surface-base hover:text-content-primary"}`;
}

function ItemContent({ item, variant }: Readonly<{ item: ListNavItem; variant: "column" | "list" }>) {
  return (
    <>
      {item.icon && cloneElement(item.icon, { size: variant === "list" ? 18 : 16, className: "shrink-0" })}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {variant === "list" && <ChevronRight size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />}
    </>
  );
}

/**
 * Grouped navigation for list → detail areas (Admin, Settings). Each item's
 * label is the link itself, so rows never nest interactive content.
 */
export function ListNav({ groups, "aria-label": ariaLabel, variant = "column" }: Readonly<ListNavProps>) {
  const isList = variant === "list";
  return (
    <nav aria-label={ariaLabel} className={isList ? "flex flex-col gap-6" : "flex flex-col gap-5"}>
      {visibleGroups(groups).map((group) => {
        const headingId = `list-nav-${group.id}`;
        return (
          <section key={group.id} aria-labelledby={headingId} className="flex flex-col gap-1">
            <h2
              id={headingId}
              className={`[font:var(--type-eyebrow)] text-content-tertiary uppercase tracking-wide ${isList ? "px-4" : "px-3"}`}
            >
              {group.label}
            </h2>
            <ul
              className={
                isList
                  ? "divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-surface-primary"
                  : "flex flex-col gap-0.5"
              }
            >
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} className={isList ? LIST_ROW : columnRowClass}>
                    <ItemContent item={item} variant={variant} />
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </nav>
  );
}
